import { createContext, ReactNode, useContext, useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { IUserAuth } from 'entities';
import { ICorrespondenceViewModel } from 'viewModels';
import CorrespondenceController from '../controller/CorrespondenceController';
import { useAuth } from './auth';
import { TypeUser } from '../types/enums';
import { CompanyController } from '../controller/CompanyController';

type ProviderProps = {
  children: ReactNode;
};

type CorrespondencesContextProps = {
  correspondences: ICorrespondenceViewModel[];
  setCorrespondences: React.Dispatch<React.SetStateAction<ICorrespondenceViewModel[]>>;
  correspondencesSelectedByUser: ICorrespondenceViewModel[];
  setCorrespondencesSelectedByUser: React.Dispatch<
    React.SetStateAction<ICorrespondenceViewModel[]>
  >;
};

const CorrespondencesContext = createContext<CorrespondencesContextProps>(
  {} as CorrespondencesContextProps,
);

const CorrespondencesProvider = ({ children }: ProviderProps) => {
  const { user, tokenIsValid, setUser } = useAuth();
  const [correspondencesSelectedByUser, setCorrespondencesSelectedByUser] = useState<
    ICorrespondenceViewModel[]
  >([] as ICorrespondenceViewModel[]);
  const [correspondences, setCorrespondences] = useState<ICorrespondenceViewModel[]>(
    [] as ICorrespondenceViewModel[],
  );

  useQuery('correspondences', () => CorrespondenceController.getAll(), {
    onSuccess: (data) => {
      setCorrespondences(data.sort((a, b) => a.name.localeCompare(b.name)));
    },
    refetchOnWindowFocus: false,
    enabled: !!user.id && tokenIsValid,
  });

  useQuery(
    'correspondences-collaborator',
    () =>
      CompanyController.getCorrespondencesByProfileId(user.collaboratorInfo?.profileId as string),
    {
      onSuccess: (data) => {
        setCorrespondencesSelectedByUser(data);
        setUser((prev) => {
          return {
            ...prev,
            collaboratorInfo: {
              ...prev.collaboratorInfo,
              profileInfo: {
                ...prev.collaboratorInfo?.profileInfo,
                correspondencesId: data.map((correspondence) => correspondence.id),
              },
            },
          } as IUserAuth;
        });
      },
      refetchOnWindowFocus: false,
      enabled:
        !!user.id &&
        !!user.collaboratorInfo?.profileId &&
        user.type === TypeUser.COLLABORATOR &&
        tokenIsValid,
    },
  );

  useQuery('correspondences-by-user', () => CorrespondenceController.getCorrespondencesByUser(), {
    onSuccess: (data) => {
      setCorrespondencesSelectedByUser(data);
    },
    refetchOnWindowFocus: false,
    enabled: !!user.id && tokenIsValid && user.type !== TypeUser.COLLABORATOR,
  });

  useEffect(() => {
    if (!tokenIsValid) {
      setCorrespondences([]);
      setCorrespondencesSelectedByUser([]);
    }
  }, [tokenIsValid]);

  const contextValues = useMemo(
    () => ({
      correspondences,
      setCorrespondences,
      correspondencesSelectedByUser,
      setCorrespondencesSelectedByUser,
    }),
    [correspondences, correspondencesSelectedByUser],
  );
  return (
    <CorrespondencesContext.Provider value={contextValues}>
      {children}
    </CorrespondencesContext.Provider>
  );
};

export default CorrespondencesProvider;

export const useCorrespondences: () => CorrespondencesContextProps = () =>
  useContext(CorrespondencesContext);
